/**
 * Cache keys, kept in one place so invalidation cannot drift.
 *
 * They live apart from the hooks because the provider that owns the cache also
 * needs them — to drop everything when a session ends, and to re-read `/me`
 * when the server says the account may not act.
 */

import type { CpuRunListQuery, CpuTaskState, JobListQuery, AuditQuery } from "./types";

/** Prefix of every CPU workspace key, so one invalidation reaches them all. */
export const CPU_WORKSPACES_KEY = ["cpu-workspaces"] as const;

export const queryKeys = {
  authConfig: ["auth", "config"] as const,
  capacity: ["capacity"] as const,
  myWorkspaces: ["workspaces", "mine"] as const,
  workspace: (id: string) => ["workspaces", id] as const,
  workspaceLogs: (id: string) => ["workspaces", id, "logs"] as const,
  environments: ["environments"] as const,
  me: ["me"] as const,
  relay: ["relay"] as const,
  // Deliberately not under `me`: the profile is re-read on events that say
  // nothing about credentials (the console being paused, for one), and a
  // prefix match would turn each of those into a second request for a list
  // only the Account page ever shows.
  consoleKeys: ["console-keys"] as const,
  jobs: (query: JobListQuery) => ["jobs", query] as const,
  job: (id: string) => ["jobs", id] as const,
  jobLogs: (id: string) => ["jobs", id, "logs"] as const,
  cpuRuns: (query: CpuRunListQuery) => ["cpu-runs", query] as const,
  cpuRun: (id: string) => ["cpu-runs", id] as const,
  // The filter is part of the key: a filtered page is its own keyset walk, and
  // two filters share no cursor. Under the run's own prefix, so stopping a run
  // re-reads its tasks with everything else it changed.
  cpuRunTasks: (id: string, state: CpuTaskState | "all") =>
    ["cpu-runs", id, "tasks", state] as const,
  // The task index is part of the key: there is no merged log of a run's
  // tasks, so two tasks of one run are two different documents.
  cpuRunLogs: (id: string, task: number) =>
    ["cpu-runs", id, "logs", task] as const,
  // The fourth kind keys on its own path, never on the workspace one: a CPU
  // workspace and a GPU workspace are two resources, and one cache entry for
  // both would serve whichever answered last.
  myCpuWorkspaces: ["cpu-workspaces", "mine"] as const,
  cpuWorkspace: (id: string) => ["cpu-workspaces", id] as const,
  cpuWorkspaceLogs: (id: string) => ["cpu-workspaces", id, "logs"] as const,
  adminUsers: ["admin", "users"] as const,
  adminUsage: (days: number) => ["admin", "usage", days] as const,
  myUsage: (days: number) => ["me", "usage", days] as const,
  adminWorkspaces: ["admin", "workspaces"] as const,
  adminAudit: (query: AuditQuery) => ["admin", "audit", query] as const,
};
